import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import CardLegalText from "../CardLegalText";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import React from "react";
import RenderImage from "../RenderImage";
import PricingSection from "../PricingSection";
import { EqualHeightElement } from "react-equal-height/clean";
import OfferRibbon from "../OfferRibbon";
import CompareBox from "../CompareBox";
import { FrontCardProps } from "../../../flip-cards.props";
import { colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import FusionTheme from "@directv-digital/fusion.themes.fusion";

const FOCUS_RING_SX = {
  "& a:focus-visible, & button:focus-visible": {
    outline: `2px solid ${colorPalette.blue[600]}`,
    outlineOffset: "2px",
    borderRadius: FusionTheme.fusion.shapes["shape-extra-small"]
  }
};

// All mobile-only layout variants are expressed as xs/sm sx breakpoints
// (xs ≡ the useBreakPoints isMobile range, < 600px) so SSR markup, hydration
// and resize all resolve in CSS without re-rendering.
const styles = {
  container: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    borderRadius: "24px",
    overflow: "hidden",
    flex: 1
  },
  inner: {
    background: "radial-gradient(circle farthest-corner at bottom right, #0062E3, #121212)",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    gap: { md: "24px" },
    pt: "32px",
    px: {
      xs: "0",
      md: "24px"
    },
    pb: {
      xs: "20px",
      sm: "16px",
      md: "26px"
    }
  },
  imageSection: {
    mb: {
      xs: "26px",
      md: "32px"
    }
  },
  bodySection: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    px: {
      xs: "24px",
      sm: "16px",
      md: "0"
    },
    flex: 1
  },
  titleWrapper: {
    display: "flex",
    flexDirection: "column"
  }
};

// Rendered height of the CompareBox row (8px top/bottom padding + ~20px icon/text row).
// The compare bar is a sibling *below* `inner`, so cards without it let `inner` fill the
// full card height and their bottom-anchored price row drops by this amount. Reserving the
// same space as inner bottom-padding keeps the price row aligned with sibling compare cards.
const COMPARE_BAR_HEIGHT = "36px";

const FrontCard = (props: FrontCardProps) => {
  const { card, isFlippable, compareBox, cardId, isMobile } = props;

  const mobileImagePosition = card?.mobileImagePosition;
  const isSideImage = mobileImagePosition === "left" || mobileImagePosition === "right";
  const sideImageWidth = isSideImage ? {
    width: {
      xs: "40%",
      sm: "auto"
    }
  } : {};
  const sideBodyWidth = isSideImage ? {
    width: {
      xs: "60%",
      sm: "auto"
    }
  } : {};
  const centerText = mobileImagePosition === "top" ? {
    textAlign: {
      xs: "center",
      sm: "left"
    }
  } : {};

  // Cards in the same row may or may not have a compare bar; reserve its height when absent
  // so every card's price row lands at the same distance from the card bottom.
  const hasCompareBox = Boolean(compareBox?.content);

  // cardRegionLabel names the group by title only — subHeading is rendered as visible
  // non-hidden content so the screen reader announces it naturally when focused.
  // Including it here would cause a double-announcement.
  const cardRegionLabel = card?.title?.content || '';

  try {
    return (
      // Single a11y group per face. This container-level group is load-bearing for the
      // static (non-flippable) tile: its title is aria-hidden and there is no flip-button
      // aria-label, so this group is the tile's ONLY accessible name — do not remove it.
      <DtvBox sx={styles.container} role="group" aria-label={cardRegionLabel || 'Package card'}>
        {/* Card background inner */}
        <DtvBox
          sx={{
            ...styles.inner,
            ...(card?.background && { background: card.background }),
            // No compare bar below this card → pad `inner` by the bar height so the
            // gradient still fills the card and the price row aligns with compare cards.
            // xs is stacked (no cross-card alignment) so its padding is left untouched.
            ...(!hasCompareBox && {
              pb: {
                xs: styles.inner.pb.xs,
                sm: `calc(${styles.inner.pb.sm} + ${COMPARE_BAR_HEIGHT})`,
                md: `calc(${styles.inner.pb.md} + ${COMPARE_BAR_HEIGHT})`
              }
            }),
            ...(mobileImagePosition === "left" && {
              flexDirection: {
                xs: "row",
                sm: "column"
              }
            }),
            ...(mobileImagePosition === "right" && {
              flexDirection: {
                xs: "row-reverse",
                sm: "column"
              }
            })
          }}
        >
          <OfferRibbon cardType="front" isFlippable={isFlippable} offerRibbon={card?.offerRibbon} />

          {/* Image section */}
          <DtvBox sx={{
            ...styles.imageSection,
            ...sideImageWidth
          }}>
            <RenderImage {...card?.image} showReflection />
          </DtvBox>

          {/* Title, description & pricing section */}
          <DtvBox sx={{
            ...styles.bodySection,
            ...sideBodyWidth
          }}>
            {/* flex:1 makes this wrapper absorb all variable space (due to image-height
                differences), so the pricing section below always lands at a consistent
                distance from the card bottom — no mt:auto needed. */}
            <DtvBox sx={{
              flex: {
                xs: "none",
                sm: 1
              }
            }}>
              <EqualHeightElement disable={isMobile} name="card-front-title-section">
                {/* No role here — the card-level container group (above) is the single
                    labeled group. A nested group with the same aria-label re-announced the
                    title (the Round-4 a11y regression); keep this a plain layout wrapper. */}
                <DtvBox sx={styles.titleWrapper}>
                  {card?.title && (
                    <DtvText {...card.title} role="heading" aria-level={2} aria-hidden="true" sx={{
                      mb: "4px",
                      ...centerText
                    }} />
                  )}
                  {(card?.subHeading?.content || card?.subHeading?.cta?.label) && (
                    <DtvText component="p" variant={card.subHeading.variant || "smalltext"} content={card.subHeading.content} cta={card.subHeading.cta} color={card?.subHeading?.color || undefined} sx={{
                      mb: "2px",
                      ...centerText,
                      ...FOCUS_RING_SX
                    }} />
                  )}
                  {card?.subHeading?.spanText?.content && (
                    <DtvText component="p" {...card.subHeading.spanText} sx={{
                      ...centerText,
                      ...FOCUS_RING_SX
                    }} />
                  )}
                </DtvBox>
              </EqualHeightElement>
            </DtvBox>
            <DtvBox>
              <EqualHeightElement disable={isMobile} name="card-pricing-legal">
                <DtvBox sx={{
                  display: "flex",
                  flexDirection: "column"
                }}>
                  <PricingSection cardType="front" cta={card?.cta} isMobile={isMobile} mobileImagePosition={card?.mobileImagePosition} priceText={card?.priceText} strikeThroughPrice={card?.strikeThroughPrice} cardName={card?.title?.content} />
                  {card?.legalText?.content && (
                    <CardLegalText legalText={card.legalText} cardId={cardId} side="front" />
                  )}
                </DtvBox>
              </EqualHeightElement>
            </DtvBox>
          </DtvBox>
        </DtvBox>

        {/* Compare Box - outside inner to avoid card background */}
        {compareBox?.content && <CompareBox cardId={cardId} cardType="front" compareBox={compareBox} cardName={card?.title?.content} />}
      </DtvBox>
    );
  } catch (error) {
    return <ErrorDisplay component="FrontCard" error={error} />;
  }
};

export default React.memo(FrontCard);
